// ./src/components/features/CreateEventWizard/steps/ArtistsStep.tsx
//
// "Who is performing or speaking?" — shown when the Artists & speakers feature is on (ROADMAP A2b #62 P6(f)). Each act is
// booked in the Artists page's own `EventArtistBookingDrawer` (from the artist catalog, or just a typed name), under a
// provider scoped by the event — the drawer saves THROUGH that provider and does nothing without it.
// ⓘ Before Budget: a booking's fee goes into the event budget when the host keeps "Track this cost in the event budget".
//   Before the Event page step, which offers the Lineup block once an act is booked.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { EventArtistsProvider, useEventArtistsContext } from '@/contexts';
import { useCreateGate, useToast } from '@/hooks';
import { EventArtist, billingLabel, bookingStatusLabel, eventArtistDisplayName, WHOLE_SET } from '@/utils/api';
import { ConfirmModal } from '@/components/generic';
import { EventArtistBookingDrawer } from '@/components/features/Artists';
import { refusalReason } from '@/utils';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';

/** The bill's order, as on the Artists page: billing (headliner first), then the host's own order. */
const byBill = (a: EventArtist, b: EventArtist): number =>
  (a.billingIndex ?? Number.MAX_SAFE_INTEGER) - (b.billingIndex ?? Number.MAX_SAFE_INTEGER) || (a.orderNo ?? 0) - (b.orderNo ?? 0);

const ArtistsStepBody: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { saving } = useEventWizard();
  const { eventArtists, list, isLoaded, remove } = useEventArtistsContext();
  // Booking acts is the `commercial` bundle — the Artists page asks the same.
  const gate = useCreateGate(FEATURE_CODES.COMMERCIAL, { eventId });

  const [readError, setReadError] = useState<OpResult | null>(null);
  const [drawer, setDrawer] = useState<{ recordId: string | null } | null>(null);
  const [pendingRemove, setPendingRemove] = useState<EventArtist | null>(null);
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    const res = await list({ eventId: [eventId] }, { page: 1, pageSize: WHOLE_SET }, undefined, true);

    setReadError(res.didSucceed() ? null : res);
  }, [list, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(() => [...eventArtists].sort(byBill), [eventArtists]);

  const afterSave = () => {
    setDrawer(null);
    void load();
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    const res = await remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void load();
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t('events:wizard.artists.intro', {
          defaultValue:
            'Book the acts and speakers for this event — from your artist catalog, or just by name. A booking’s fee can go straight into the event budget, and the Event page step offers a Lineup block to show them.',
        })}
      </Text>

      <WizardListSection
        title={t('events:wizard.artists.title', { defaultValue: 'Artists & speakers' })}
        addLabel={t('events:wizard.artists.add', { defaultValue: 'Book an act' })}
        onAdd={() => gate.canCreate && setDrawer({ recordId: null })}
        gate={gate}
        gateWhat={t('events:wizard.artists.another', { defaultValue: 'another act' })}
        disabled={saving}
        isLoaded={isLoaded || !!readError}
        readError={readError}
        onRetry={() => void load()}
        errorTitle={t('events:wizard.artists.loadFailed', { defaultValue: 'Could not load the lineup' })}
        operation="eventArtistList"
        emptyText={t('events:wizard.artists.none', { defaultValue: 'No acts booked yet.' })}
        isEmpty={sorted.length === 0}
      >
        {sorted.map((a) => (
          <WizardItemRow
            key={a.id}
            icon="Microphone"
            title={eventArtistDisplayName(a)}
            subtitle={[billingLabel(a.billing), bookingStatusLabel(a.bookingStatus)].filter(Boolean).join(' · ') || null}
            disabled={saving}
            onEdit={() => setDrawer({ recordId: a.id as string })}
            onRemove={() => setPendingRemove(a)}
          />
        ))}
      </WizardListSection>

      <EventArtistBookingDrawer
        opened={!!drawer}
        onClose={() => setDrawer(null)}
        eventId={eventId}
        recordId={drawer?.recordId ?? null}
        onCreated={afterSave}
        onUpdated={afterSave}
      />

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.artists.removeTitle', { defaultValue: 'Remove this act?' })}
        message={t('events:wizard.tickets.removeMessage', {
          defaultValue: '“{{name}}” will be removed from this event.',
          name: pendingRemove ? eventArtistDisplayName(pendingRemove) : '',
        })}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const ArtistsStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <EventArtistsProvider initialFilters={{ eventId: [createdEventId] }} pageSize={WHOLE_SET} autoLoad={false}>
      <ArtistsStepBody eventId={createdEventId} />
    </EventArtistsProvider>
  );
};
